import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Phone, ChevronDown, Mail, ArrowRight } from 'lucide-react';
import SmartStrip from '../recommendations/SmartStrip';
import { useAuthModal } from '../../context/AuthModalContext';
import { SEO_INVENTORY, getTotalInventoryCount } from '../../data/seoInventory';
import { useUserState, isLiveState } from '../../lib/userState';
import PreferredSourceButton from '../PreferredSourceButton';
import { AppStoreRating } from '../trust/AppStoreRating';

// ============================================================
// Top "curated" footer - 4 clean columns. State-aware: the
// "Cornerstone reads" list, the "Shortlist a business" Home & Land
// link and the "Get in touch" intro all swap for VIC visitors so
// the footer never shows Adelaide-tagged links to someone who
// picked Victoria. Legal / operator lines below stay literal
// (GSTUDIO Pty Ltd is a South Australian entity, disputes are
// governed by SA law - those are facts, not marketing).
// ============================================================
const SA_CORNERSTONE_READS = [
  { name: "Adelaide build cost report 2026", href: "/adelaide-build-cost-report-2026", thumb: "/images/heroes/new homes.webp" },
  { name: "Best home builders Adelaide", href: "/home-builders-adelaide", thumb: "/images/heroes/trusted-builders-hero.jpg" },
  { name: "Knockdown rebuild guide", href: "/knockdown-rebuild", thumb: "/images/heroes/knockdown rebuild.webp" },
  { name: "House and land packages", href: "/house-and-land-packages-adelaide", thumb: "/images/heroes/choosing land.webp" },
  { name: "First home buyer (Adelaide)", href: "/first-home-buyer-adelaide", thumb: "/images/heroes/home design.webp" },
  { name: "Building glossary", href: "/terminology", thumb: "/images/heroes/interior design.webp" },
];

const VIC_CORNERSTONE_READS = [
  { name: "Building in Melbourne 2026", href: "/post/building-in-melbourne-2026-what-buildpilot-covers-today", thumb: "/images/regions-vic/inner-melbourne.jpg" },
  { name: "VIC first-home grants + concessions", href: "/vic/grants", thumb: "/images/regions-vic/eastern-melbourne.jpg" },
  { name: "Building in Melbourne (Council)", href: "/vic/building-in/melbourne-council-vic", thumb: "/images/regions-vic/inner-melbourne.jpg" },
  { name: "Building in Yarra", href: "/vic/building-in/yarra-council-vic", thumb: "/images/regions-vic/northern-melbourne.jpg" },
  { name: "Building in Port Phillip", href: "/vic/building-in/port-phillip-council-vic", thumb: "/images/regions-vic/mornington-peninsula.jpg" },
  { name: "Building glossary", href: "/terminology", thumb: "/images/heroes/interior design.webp" },
];

// Neutral cornerstone reads for non-live states. Every entry is
// universal (no Adelaide or Melbourne in the label or href) so a
// visitor from NSW/QLD/WA never sees hand-me-down state content in
// the footer.
const UNIVERSAL_CORNERSTONE_READS = [
  { name: "Building a new home", href: "/building-a-new-home", thumb: "/images/heroes/new homes.webp" },
  { name: "Choosing the right land", href: "/choosing-land", thumb: "/images/heroes/choosing land.webp" },
  { name: "Knockdown rebuild guide", href: "/knockdown-rebuild", thumb: "/images/heroes/knockdown rebuild.webp" },
  { name: "Renovations", href: "/renovations", thumb: "/images/heroes/interior design.webp" },
  { name: "Home designs & floor plans", href: "/home-designs", thumb: "/images/heroes/home design.webp" },
  { name: "Building glossary", href: "/terminology", thumb: "/images/heroes/interior design.webp" },
];

// Portal-style location directory. SA suburbs resolve to the canonical
// /home-builders-in/<slug> pages; VIC uses the /vic/building-in council
// guides. Kept short + curated (state-gated at render).
const SA_SUBURB_LINKS = [
  { name: 'Mount Barker', href: '/home-builders-in/mount-barker' },
  { name: 'Gawler', href: '/home-builders-in/gawler' },
  { name: 'Seaford', href: '/home-builders-in/seaford' },
  { name: 'Angle Vale', href: '/home-builders-in/angle-vale' },
  { name: 'Aldinga', href: '/home-builders-in/aldinga' },
  { name: 'Munno Para', href: '/home-builders-in/munno-para' },
  { name: 'Two Wells', href: '/home-builders-in/two-wells' },
  { name: 'Craigmore', href: '/home-builders-in/craigmore' },
  { name: 'Mawson Lakes', href: '/home-builders-in/mawson-lakes' },
  { name: 'Victor Harbor', href: '/home-builders-in/victor-harbor' },
];
const VIC_SUBURB_LINKS = [
  { name: 'Melbourne (City)', href: '/vic/building-in/melbourne-council-vic' },
  { name: 'Yarra', href: '/vic/building-in/yarra-council-vic' },
  { name: 'Port Phillip', href: '/vic/building-in/port-phillip-council-vic' },
  { name: 'Stonnington', href: '/vic/building-in/stonnington-council-vic' },
  { name: 'Glen Eira', href: '/vic/building-in/glen-eira-council-vic' },
  { name: 'Monash', href: '/vic/building-in/monash-council-vic' },
  { name: 'Boroondara', href: '/vic/building-in/boroondara-council-vic' },
  { name: 'Bayside', href: '/vic/building-in/bayside-council-vic' },
  { name: 'Whitehorse', href: '/vic/building-in/whitehorse-council-vic' },
  { name: 'Manningham', href: '/vic/building-in/manningham-council-vic' },
];
const VIC_CORRIDOR_LINKS = [
  { name: 'Wyndham', href: '/home-builders-wyndham' },
  { name: 'Melton', href: '/home-builders-melton' },
  { name: 'Casey', href: '/home-builders-casey' },
  { name: 'Hume', href: '/home-builders-hume' },
  { name: 'Whittlesea', href: '/home-builders-whittlesea' },
  { name: 'Cardinia', href: '/home-builders-cardinia' },
];

const curatedColumnsForState = (userState) => {
  const isVic = userState === 'VIC';
  const isSa = userState === 'SA';
  const hnl = isVic
    ? { name: "Explore the Victoria hub", href: "/vic" }
    : isSa
      ? { name: "Search Home & Land", href: "/house-and-land-adelaide" }
      : { name: "Building glossary", href: "/terminology" };
  const cornerstoneLinks = isVic
    ? VIC_CORNERSTONE_READS
    : isSa
      ? SA_CORNERSTONE_READS
      : UNIVERSAL_CORNERSTONE_READS;
  return [
    {
      title: "Shortlist a business",
      intro: "BuildPilot personally shortlists the right business for your build.",
      links: [
        { name: "Shortlist a Builder", href: "/find-a-builder", highlight: true },
        { name: "Shortlist a Supplier", href: "/suppliers" },
        { name: "Shortlist a Tradie", href: "/find/category/tradies" },
        { name: "Shortlist a Professional", href: "/find/category/professionals" },
        { name: "Compare home designs", href: "/designs" },
        hnl,
      ],
    },
    {
      title: "Cornerstone reads",
      links: cornerstoneLinks,
    },
    {
      title: "BuildPilot",
      links: [
        { name: "One on One Support", href: "/one-on-one-support", highlight: true, subtitle: "real human · contract to keys" },
        { name: "Sign in", action: "auth:signin" },
        { name: "Create account", action: "auth:signup" },
        { name: "Premium pricing", href: "/workspace/paywall" },
        { name: "Why BuildPilot", href: "/why-buildpilot" },
        { name: "Where we fit in", href: "/where-we-fit-in" },
      ],
    },
    {
      title: "Get in touch",
      links: [
        { name: "Contact us", href: "/contact" },
        { name: "Help & advice", href: "/help" },
        { name: "Watch: Home Building Podcast", href: "/watch" },
        { name: "Our community", href: "/community" },
        { name: "List your business", href: "/business", highlight: true },
        { name: "Business partners", href: "/partners" },
      ],
    },
  ];
};

// Overflow links trimmed from the curated columns. Still rendered site-wide
// (a quiet wrap row above the bottom bar) so none of the SEO internal-link
// equity is lost - the columns just stay crisp.
const OVERFLOW_LINKS = [
  { name: "How BuildPilot works", href: "/how-it-works" },
  { name: "How we make money", href: "/about/how-we-make-money" },
  { name: "Awards & achievements", href: "/awards" },
  { name: "As seen in", href: "/media" },
  { name: "Editorial process", href: "/about/editorial-process" },
  { name: "Been referred to us?", href: "/referred" },
  { name: "Complaints process", href: "/legal/complaints-process" },
  { name: "Affiliate disclosure", href: "/affiliate-disclosure" },
];

// ============================================================
// "More from BuildPilot" - the full SEO inventory.
// Lives inside an accordion so users see a clean footer
// while Google still gets the full crawl footprint.
// ============================================================
const seoInventory = SEO_INVENTORY;

// Slim inline newsletter capture for the footer top band. Posts to the same
// endpoint as the homepage NewsletterSignup (source="footer").
const FooterNewsletter = () => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | submitting | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === 'submitting') return;
    setState('submitting');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer', privacy_consent: true, source_page: '/' }),
      });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <p className="text-[13.5px] text-white" data-testid="footer-newsletter-success">
        You're in — the latest building news is on its way. ✓
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-[400px]" data-testid="footer-newsletter">
      <label className="block text-[13px] text-gray-300 mb-2">The latest building industry news &amp; tips.</label>
      <div className="flex items-center gap-2 bg-white/10 border border-white/15 rounded-full p-1.5 pl-4 focus-within:border-[#1d8efe] transition-colors">
        <Mail className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="bg-transparent flex-1 min-w-0 text-[14px] text-white placeholder-gray-500 py-1.5 outline-none"
          data-testid="footer-newsletter-email"
          autoComplete="email"
        />
        <button
          type="submit"
          disabled={state === 'submitting'}
          className="shrink-0 inline-flex items-center justify-center gap-1 bg-[#1d8efe] hover:bg-[#1676d1] text-white text-[13px] font-semibold px-4 py-2 min-h-[40px] rounded-full transition-colors disabled:opacity-60"
          data-testid="footer-newsletter-submit"
        >
          {state === 'submitting' ? '…' : <>Join <ArrowRight className="w-3.5 h-3.5" /></>}
        </button>
      </div>
      {state === 'error' && (
        <p className="text-[12px] text-[#ffb3b3] mt-1.5">Something went wrong — please try again.</p>
      )}
      <p className="text-[11px] text-gray-500 mt-2">
        By subscribing you agree to our{' '}
        <Link to="/privacy" className="underline hover:text-gray-300">Privacy Policy</Link>.
      </p>
    </form>
  );
};

const Footer = () => {
  const [expandedInventory, setExpandedInventory] = useState(false);
  const authModal = useAuthModal();
  const userState = useUserState();
  const isVic = userState === 'VIC';
  const isSa = userState === 'SA';
  const curatedColumns = curatedColumnsForState(userState);

  const handleLinkAction = (action) => {
    if (action === 'auth:signin') return authModal.open('signin');
    if (action === 'auth:signup') return authModal.open('signup');
    return null;
  };

  return (
    <footer className="bg-black text-white">
      {/* Smart contextual recommendation rail, Phase 1.5 Opportunity
          Engine. Renders the single most-relevant intro for the
          current page + user. Auto-suppressed on legal/about/policy
          paths and on workspace routes. */}
      <div className="bg-white text-[#1d1d1f]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <SmartStrip surface="footer" />
        </div>
      </div>

      {/* Top: Curated 4-column footer */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
        <div className="mb-8 pb-8 border-b border-gray-800 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-10">
          <div>
            <Link to="/" className="inline-block">
              <img
                src="/images/buildpilot-logo-2026.png"
                alt="BuildPilot"
                width="180" height="36"
                className="h-[30px] sm:h-[34px] w-auto" loading="eager" decoding="async" fetchPriority="high" />
            </Link>
            <p className="text-gray-400 text-[14px] sm:text-[15px] mt-3 leading-[1.5] max-w-sm" data-testid="footer-tagline">
              Australia's home for building.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://instagram.com/buildpilot.com.au" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-[#1d8efe] rounded-full flex items-center justify-center transition-all duration-300" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/buildpilot.com.au" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-[#1d8efe] rounded-full flex items-center justify-center transition-all duration-300" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.linkedin.com/company/buildpilotau/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-[#1d8efe] rounded-full flex items-center justify-center transition-all duration-300" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="tel:1300885919" className="w-10 h-10 bg-gray-800 hover:bg-[#1d8efe] rounded-full flex items-center justify-center transition-all duration-300" aria-label="Call us" data-testid="footer-call-btn">
                <Phone className="w-4 h-4" />
              </a>
              <a href="mailto:home@buildpilot.com.au" className="w-10 h-10 bg-gray-800 hover:bg-[#1d8efe] rounded-full flex items-center justify-center transition-all duration-300" aria-label="Email us" data-testid="footer-email-btn">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="w-full lg:w-auto lg:min-w-[380px] flex flex-col gap-3 lg:items-start">
            <FooterNewsletter />
            <div className="flex flex-col gap-2.5">
              <AppStoreRating variant="compact" dark />
              <div className="flex items-center justify-start sm:justify-between gap-3 sm:gap-3">
                <a href="https://apps.apple.com/au/app/buildpilot/id6477372083" target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" className="flex items-center" data-testid="footer-appstore-link">
                  <img src="/images/app/app-store-badge.svg" alt="Download BuildPilot on the Apple App Store" className="h-[34px] sm:h-[40px] w-auto" loading="lazy" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US" target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" className="flex items-center" data-testid="footer-googleplay-link">
                  <img src="/images/app/google-play-badge-tight.png" alt="Get BuildPilot on Google Play" className="h-[34px] sm:h-[40px] w-auto" loading="lazy" />
                </a>
                <PreferredSourceButton variant="badge" data-testid="footer-preferred-source" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-8">
          {curatedColumns.map((col) => (
            <div key={col.title}>
              <h3 className="text-[13px] font-semibold text-white uppercase tracking-[0.05em] mb-4">
                {col.title}
              </h3>
              {col.intro && (
                <p className="text-[13px] text-gray-500 mb-5 leading-[1.6]">{col.intro}</p>
              )}
              {col.showThumbnails ? null : (
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.action || link.href}>
                      {link.action ? (
                        <button
                          type="button"
                          onPointerDown={(e) => e.preventDefault()}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => { e.preventDefault(); handleLinkAction(link.action); }}
                          className={`inline-flex items-center text-[14px] text-left transition-colors duration-300 ${
                            link.highlight
                              ? 'text-[#1d8efe] hover:text-white font-medium'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          data-testid={`footer-action-${link.action.replace(':', '-')}`}
                        >
                          {link.name}
                        </button>
                      ) : (
                        <Link
                          to={link.href}
                          className={`group inline-flex flex-col text-[14px] transition-colors duration-300 ${
                            link.highlight
                              ? 'text-[#1d8efe] hover:text-white font-medium'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          <span>{link.name}</span>
                          {link.subtitle && (
                            <span className="text-[11.5px] text-gray-500 group-hover:text-gray-300 transition-colors mt-0.5 font-normal tracking-normal">
                              {link.subtitle}
                            </span>
                          )}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Overflow links trimmed from the columns - kept crawlable site-wide. */}
        <nav className="mt-10 pt-8 border-t border-gray-800 flex flex-wrap gap-x-6 gap-y-2.5" data-testid="footer-overflow-links" aria-label="More BuildPilot links">
          {OVERFLOW_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="text-[12.5px] text-gray-500 hover:text-white transition-colors">
              {link.name}
            </Link>
          ))}
          <button
            type="button"
            data-testid="footer-cookie-preferences"
            onClick={() => { if (typeof window !== 'undefined' && window.bpOpenCookieSettings) window.bpOpenCookieSettings(); }}
            className="text-[12.5px] text-gray-500 hover:text-white transition-colors"
          >
            Cookie preferences
          </button>
        </nav>
      </div>

      {/* Browse by location - national portal-style suburb directory.
          State-gated: SA visitors see Adelaide suburbs, VIC visitors see
          Melbourne councils, non-live visitors see both live states. */}
      <div className="border-t border-gray-800">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <h3 className="text-[13px] font-semibold text-white uppercase tracking-[0.05em] mb-6">
            Browse builders by location
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {(isSa || (!isSa && !isVic)) && (
              <div data-testid="footer-suburbs-sa">
                <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.06em] mb-4">
                  South Australia
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5">
                  {SA_SUBURB_LINKS.map((s) => (
                    <Link key={s.href} to={s.href} className="text-[13px] text-gray-400 hover:text-white transition-colors">
                      {s.name}
                    </Link>
                  ))}
                  <Link to="/home-builders-adelaide" className="text-[13px] text-[#1d8efe] hover:text-white transition-colors font-medium">
                    All Adelaide suburbs →
                  </Link>
                </div>
              </div>
            )}
            {(isVic || (!isSa && !isVic)) && (
              <div data-testid="footer-suburbs-vic">
                <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.06em] mb-4">
                  Victoria
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5">
                  {VIC_SUBURB_LINKS.map((s) => (
                    <Link key={s.href} to={s.href} className="text-[13px] text-gray-400 hover:text-white transition-colors">
                      {s.name}
                    </Link>
                  ))}
                  <Link to="/vic" className="text-[13px] text-[#1d8efe] hover:text-white transition-colors font-medium">
                    All Victoria areas →
                  </Link>
                </div>
                <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-[0.06em] mt-6 mb-4">
                  Melbourne growth corridors
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5" data-testid="footer-corridors-vic">
                  {VIC_CORRIDOR_LINKS.map((c) => (
                    <Link key={c.href} to={c.href} data-testid={`footer-corridor-${c.name.toLowerCase()}`} className="text-[13px] text-gray-400 hover:text-white transition-colors">
                      {c.name}
                    </Link>
                  ))}
                  <Link to="/melbourne-growth-corridors" data-testid="footer-corridor-compare" className="text-[13px] text-[#1d8efe] hover:text-white transition-colors font-medium">
                    Compare all corridors →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* "More from BuildPilot" - full SEO inventory accordion.
          Only shown on SA (where the 400+ page inventory is native);
          hidden on every other state so a VIC or NSW visitor is not
          shown an Adelaide sitemap. Crawlers still get every URL
          through sitemap.xml so indexation is unaffected. */}
      {isSa && (
      <div className="border-t border-gray-800">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            type="button"
            onClick={() => setExpandedInventory((v) => !v)}
            className="w-full flex items-center justify-between text-left text-gray-300 hover:text-white transition-colors group"
            aria-expanded={expandedInventory}
            data-testid="footer-inventory-toggle"
          >
            <span className="text-[13px] sm:text-[14px] font-medium uppercase tracking-[0.05em]">
              More from BuildPilot
              <span className="ml-2 normal-case font-normal tracking-normal text-gray-500">
                · full sitemap of {getTotalInventoryCount()}+ guides
              </span>
            </span>
            <ChevronDown
              className={`w-5 h-5 transition-transform duration-300 ${expandedInventory ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Always rendered for SEO/crawlability; visually hidden until expanded.
              Crawlers index the links regardless of CSS state. */}
          <div
            className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-8 mt-6 ${
              expandedInventory ? 'block' : 'hidden'
            }`}
            data-testid="footer-inventory-panel"
          >
            {Object.entries(seoInventory).map(([groupTitle, links]) => (
              <div key={groupTitle}>
                <h4 className="text-[12px] font-semibold text-white uppercase tracking-[0.05em] mb-3">
                  {groupTitle}
                </h4>
                <ul className="space-y-1.5">
                  {links.map((link) => (
                    <li key={link.href}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[13px] text-gray-400 hover:text-white transition-colors leading-[1.5]"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-[13px] text-gray-400 hover:text-white transition-colors leading-[1.5]"
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Bottom Bar - Entity Statement + social */}
      <div className="border-t border-gray-800">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Our role - publisher/introducer positioning */}
          <p className="text-gray-300 text-[14px] sm:text-[15px] leading-[1.7] mb-4 max-w-3xl" data-testid="footer-our-role">
            BuildPilot helps Australians navigate the building journey and shortlist suitable builders, designs, products and professionals. We are not a builder, contractor or owner-builder service.{' '}
            <Link to="/how-it-works" className="text-[#1d8efe] hover:text-white transition-colors underline-offset-2 hover:underline">
              How BuildPilot Works →
            </Link>
          </p>
          <p className="text-gray-400 text-[14px] sm:text-[15px] leading-[1.7] mb-6 max-w-3xl">
            {isSa ? (
              <>BuildPilot provides one-to-one building support in South Australia. BuildPilot also offers a self-guided building app available at{' '}</>
            ) : isVic ? (
              <>BuildPilot's live builder matching, house and land listings and display home directory are live across South Australia and Victoria. Our self-guided building app is national and works today at{' '}</>
            ) : (
              <>BuildPilot's live builder matching is live in South Australia and Victoria. Our self-guided building app is national and works today at{' '}</>
            )}
            <a
              href="https://buildpilot.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1d8efe] hover:text-white transition-colors"
            >
              buildpilot.app
            </a>.
          </p>

          {/* Builder licensing & consumer protection references. Points to
              the official state regulators so buyers can independently verify
              a builder's licence. State-gated. Factual - not a BuildPilot claim. */}
          <div
            className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-gray-400"
            data-testid="footer-licensing"
          >
            <span className="font-semibold text-gray-300">Verify a builder's licence &amp; consumer protections:</span>
            {(isSa || (!isSa && !isVic)) && (
              <a
                href="https://www.cbs.sa.gov.au/find-licence-or-registration"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-licensing-cbs"
              >
                SA · Consumer and Business Services (CBS)
              </a>
            )}
            {(isVic || (!isSa && !isVic)) && (
              <a
                href="https://www.vba.vic.gov.au"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-licensing-vba"
              >
                VIC · Victorian Building Authority (VBA)
              </a>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <p className="text-gray-500 text-[13px] sm:text-[14px]">
              © {new Date().getFullYear()} GSTUDIO Pty Ltd, trading as BuildPilot. All rights reserved.
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/sitemap"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-sitemap-link"
              >
                Sitemap
              </Link>
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/sources"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-sources-link"
              >
                Source Index
              </Link>
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/editorial-notebook"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-notebook-link"
              >
                Editorial Notebook
              </Link>
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/sa-market-notes"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-market-notes-link"
              >
                Market Notes
              </Link>
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/new-this-month"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-new-this-month-link"
              >
                New This Month
              </Link>
              <span className="mx-2 text-gray-700">·</span>
              <Link
                to="/build-choices"
                className="text-gray-400 hover:text-white transition-colors underline-offset-2 hover:underline"
                data-testid="footer-build-choices-link"
              >
                Build Choices
              </Link>
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-gray-800 pt-6 px-5 sm:px-6 max-w-7xl mx-auto pb-8">
          <p className="text-xs text-gray-500 leading-relaxed mb-3" data-testid="footer-jurisdiction">
            <span className="font-semibold text-gray-400">Australian platform.</span>{' '}
            BuildPilot is operated by GSTUDIO Pty Ltd in South Australia and is intended for use in Australia.
            Content, builders, opportunities, regulations and consumer protections referenced on this site are Australian.
            If you access the platform from outside Australia you do so on your own initiative and at your own risk.
            BuildPilot does not warrant that the platform or its content complies with the laws of any country
            other than Australia. Any dispute is governed by the laws of South Australia, Australia.
          </p>
          <p className="text-xs text-gray-500 leading-relaxed" data-testid="footer-disclaimer">
            This website provides general information and educational content only. It does not constitute legal, financial, tax, engineering, or professional building advice.
            Laws, regulations, costs, and market conditions vary by state, council area, and individual circumstances. All cost figures are indicative and may not reflect current pricing.
            Seek independent professional advice before making building, property, or financial decisions.
            BuildPilot does not guarantee builder performance and users should conduct their own due diligence.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
